import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportUndoResult } from '@/api/models';
import { CatalogImportQueryKeys } from './catalogImportQueryKeys';
import { CATALOG_IMPORT_TOUCHED_ROOTS } from './useCatalogImportMutation';

/**
 * Undoes an import (`POST catalog-imports/{id}/undo`, OrgAdmin, while
 * onboarding is open) and resolves to the record, now `Undone`, with
 * `kept[]`: the names the undo left because something else uses them.
 *
 * Unwraps the envelope (rulebook §10): a refusal rejects with the
 * `ErrorResponse`, code intact — `IMPORT_ALREADY_UNDONE`,
 * `ONBOARDING_FINISHED`, `IMPORT_NOT_LATEST`, `IMPORT_CONSUMED`,
 * `IMPORT_NOT_FOUND`, or the books' own.
 *
 * On success it invalidates every catalog-import key (the history and the
 * record) and every root in {@link CATALOG_IMPORT_TOUCHED_ROOTS}. A refusal
 * changed nothing and invalidates nothing.
 */
export const useUndoCatalogImportMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<CatalogImportUndoResult, ErrorResponse, string>({
        mutationFn: async (id) => {
            const result = await apiClient.catalogImports.undo(id);
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: CatalogImportQueryKeys.all,
            });
            for (const queryKey of CATALOG_IMPORT_TOUCHED_ROOTS) {
                void queryClient.invalidateQueries({ queryKey });
            }
        },
    });
};
